import APIService from "./APIService";

const SimulationDataGeneratorAPI = {
  // ═══════════════════════════════════════════════════════════════════
  // GENERATION
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Generate new simulation messages via AI.
   * @param {Object} request - { communicationChannel, count, sophisticationLevelMin, sophisticationLevelMax, contexts }
   */
  generateMessages: async (request) => {
    return APIService.post(
      "/api/v1/simulation/data-generator/generate",
      request,
      { timeout: 120000 },
    );
  },

  // ═══════════════════════════════════════════════════════════════════
  // PENDING MESSAGES
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Get pending messages with optional filters.
   * @param {string} [channel] - EMAIL or SMS
   * @param {string} [status] - PENDING, APPROVED, or REJECTED
   */
  getPendingMessages: async (channel, status) => {
    const params = new URLSearchParams();
    if (channel) params.append("channel", channel);
    if (status) params.append("status", status);
    const query = params.toString();
    return APIService.get(
      `/api/v1/simulation/data-generator/pending${query ? `?${query}` : ""}`,
    );
  },

  /**
   * Get all messages in a specific batch.
   * @param {string} batchId
   */
  getPendingBatch: async (batchId) => {
    return APIService.get(
      `/api/v1/simulation/data-generator/pending/batch/${batchId}`,
    );
  },

  /**
   * Update a pending message before approval.
   * @param {string} uuid - Pending message UUID
   * @param {Object} data - Updated message fields
   */
  updatePendingMessage: async (uuid, data) => {
    return APIService.put(
      `/api/v1/simulation/data-generator/pending/${uuid}`,
      data,
    );
  },

  /**
   * Delete a single pending message.
   * @param {string} uuid - Pending message UUID
   */
  deletePendingMessage: async (uuid) => {
    return APIService.delete(
      `/api/v1/simulation/data-generator/pending/${uuid}`,
    );
  },

  // ═══════════════════════════════════════════════════════════════════
  // APPROVAL / REJECTION
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Approve selected pending messages.
   * @param {string[]} pendingUuids - Array of pending message UUIDs
   */
  approveMessages: async (pendingUuids) => {
    return APIService.post("/api/v1/simulation/data-generator/approve", {
      pendingUuids,
    });
  },

  /**
   * Approve all PENDING messages in a batch.
   * @param {string} batchId
   */
  approveBatch: async (batchId) => {
    return APIService.post(
      `/api/v1/simulation/data-generator/approve-batch/${batchId}`,
      {},
    );
  },

  /**
   * Reject selected pending messages.
   * @param {string[]} pendingUuids - Array of pending message UUIDs
   * @param {string} [reason] - Optional rejection reason
   */
  rejectMessages: async (pendingUuids, reason) => {
    return APIService.post("/api/v1/simulation/data-generator/reject", {
      pendingUuids,
      rejectionReason: reason || null,
    });
  },

  /**
   * Reject all PENDING messages in a batch.
   * @param {string} batchId
   * @param {string} [reason] - Optional rejection reason
   */
  rejectBatch: async (batchId, reason) => {
    return APIService.post(
      `/api/v1/simulation/data-generator/reject-batch/${batchId}`,
      { rejectionReason: reason || null },
    );
  },

  // ═══════════════════════════════════════════════════════════════════
  // BATCHES & STATS
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Get all generation batch summaries.
   */
  getBatchSummaries: async () => {
    return APIService.get("/api/v1/simulation/data-generator/batches");
  },

  /**
   * Get message statistics (production + pending).
   */
  getStats: async () => {
    return APIService.get("/api/v1/simulation/data-generator/stats");
  },

  // ═══════════════════════════════════════════════════════════════════
  // PRODUCTION MESSAGES (Message Library)
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Get all production messages.
   */
  getProductionMessages: async () => {
    return APIService.get("/api/v1/simulation/messages");
  },

  /**
   * Get a single production message by UUID.
   * @param {string} uuid - Message UUID
   */
  getProductionMessageById: async (uuid) => {
    return APIService.get(`/api/v1/simulation/messages/${uuid}`);
  },

  /**
   * Get production messages filtered by channel and/or context.
   * @param {Object} filters - { communicationChannel, communicationContext }
   */
  getProductionMessagesFiltered: async (filters = {}) => {
    const { communicationChannel, communicationContext } = filters;
    if (communicationChannel && communicationContext) {
      return APIService.get(
        `/api/v1/simulation/messages/filter?communicationContext=${encodeURIComponent(communicationContext)}&communicationChannel=${encodeURIComponent(communicationChannel)}`,
      );
    }
    if (communicationChannel) {
      return APIService.get(
        `/api/v1/simulation/messages/channel/${encodeURIComponent(communicationChannel)}`,
      );
    }
    if (communicationContext) {
      return APIService.get(
        `/api/v1/simulation/messages/context?communicationContext=${encodeURIComponent(communicationContext)}`,
      );
    }
    return APIService.get("/api/v1/simulation/messages");
  },

  /**
   * Search production messages by subject.
   * @param {string} query - search term to match against subject and content
   */
  searchProductionMessages: async (query) => {
    return APIService.get(
      `/api/v1/simulation/messages/search?q=${encodeURIComponent(query)}`,
    );
  },

  /**
   * Update a production message.
   * @param {string} uuid - Message UUID
   * @param {Object} data - Updated message fields
   */
  updateProductionMessage: async (uuid, data) => {
    return APIService.put(`/api/v1/simulation/messages/${uuid}`, data);
  },

  /**
   * Delete a production message.
   * @param {string} uuid - Message UUID
   */
  deleteProductionMessage: async (uuid) => {
    return APIService.delete(`/api/v1/simulation/messages/${uuid}`);
  },

  // ─── Compliance Audit & Fix ───────────────────────────────────────

  /**
   * Run a dry-run compliance audit on all production messages.
   * Reports trademark/trade-dress violations and missing disclaimers without modifying data.
   * @param {string} [channel] - Optional filter: EMAIL, SMS, VOICE, SOCIAL_MEDIA
   */
  auditCompliance: async (channel) => {
    const params = channel ? `?channel=${encodeURIComponent(channel)}` : "";
    return APIService.get(
      `/api/v1/simulation/data-generator/compliance/audit${params}`,
    );
  },

  /**
   * Apply compliance fixes to production messages — strips trade dress,
   * injects simulation disclaimers, and saves corrected messages.
   * @param {string} [channel] - Optional filter: EMAIL, SMS, VOICE, SOCIAL_MEDIA
   */
  applyComplianceFix: async (channel) => {
    const params = channel ? `?channel=${encodeURIComponent(channel)}` : "";
    return APIService.post(
      `/api/v1/simulation/data-generator/compliance/fix${params}`,
    );
  },

  // ─── Phone Number Sanitization ────────────────────────────────────

  /**
   * Dry-run scan of all production messages for phone numbers.
   * Reports every phone number found without modifying data.
   * @param {string} [channel] - Optional filter: EMAIL, SMS, VOICE, SOCIAL_MEDIA
   */
  scanPhoneNumbers: async (channel) => {
    const params = channel ? `?channel=${encodeURIComponent(channel)}` : "";
    return APIService.get(
      `/api/v1/simulation/data-generator/phone-sanitize/scan${params}`,
    );
  },

  /**
   * Apply phone-number sanitization — replaces all detected phone numbers
   * with 888-899-5004 and persists the changes.
   * @param {string} [channel] - Optional filter: EMAIL, SMS, VOICE, SOCIAL_MEDIA
   */
  applyPhoneSanitization: async (channel) => {
    const params = channel ? `?channel=${encodeURIComponent(channel)}` : "";
    return APIService.post(
      `/api/v1/simulation/data-generator/phone-sanitize/apply${params}`,
    );
  },

  /**
   * Apply phone-number sanitization for a selected subset of messages.
   * @param {string[]} uuids - Array of message UUIDs to sanitize
   */
  applyPhoneSanitizationSelected: async (uuids) => {
    return APIService.post(
      `/api/v1/simulation/data-generator/phone-sanitize/apply-selected`,
      { uuids },
    );
  },

  /**
   * Mark a message as phone-safe (exempt from future scans).
   * @param {string} messageUuid
   * @param {string} [reason] - Optional explanation
   */
  markPhoneSafe: async (messageUuid, reason) => {
    return APIService.post(
      `/api/v1/simulation/data-generator/phone-sanitize/mark-safe`,
      { messageUuid, reason: reason || null },
    );
  },

  /**
   * Remove the safe exemption from a message.
   * @param {string} messageUuid
   */
  unmarkPhoneSafe: async (messageUuid) => {
    return APIService.delete(
      `/api/v1/simulation/data-generator/phone-sanitize/mark-safe/${encodeURIComponent(messageUuid)}`,
    );
  },

  /**
   * Retrieve all phone-safe-listed messages for audit.
   */
  getPhoneSafeList: async () => {
    return APIService.get(
      `/api/v1/simulation/data-generator/phone-sanitize/safe-list`,
    );
  },
};

export default SimulationDataGeneratorAPI;
